import React, { useEffect, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import TablePagination from '@mui/material/TablePagination';
import { styled } from '@mui/material/styles';
import { Archive, Tags, Plus, Edit as EditIcon, Delete as DeleteIcon } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

const StandardBadge = styled('div')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 36,
  height: 36,
  padding: '0 8px',
  borderRadius: 6,
  backgroundColor: 'rgba(255, 171, 0, 0.16)',
  color: 'rgb(183, 110, 0)',
  fontSize: '0.85rem',
  fontWeight: 700
}));

export default function AssetCategories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    is_assignable: true,
    default_usage_type: ''
  });
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/asset-categories');
      setRows(res.data || []);
    } catch (error) {
      console.error('Error loading asset categories', error);
      toast.error('Errore nel caricamento delle categorie asset');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredSortedRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    let data = rows;
    if (term) {
      data = data.filter((row) => {
        const code = row.code || '';
        const name = row.name || '';
        const description = row.description || '';
        return (
          code.toLowerCase().includes(term) ||
          name.toLowerCase().includes(term) ||
          description.toLowerCase().includes(term)
        );
      });
    }
    const sorted = [...data].sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];
      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return order === 'asc' ? valueA - valueB : valueB - valueA;
      }
      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      if (strA < strB) return order === 'asc' ? -1 : 1;
      if (strA > strB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, search, order, orderBy]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredSortedRows.slice(start, start + rowsPerPage);
  }, [filteredSortedRows, page, rowsPerPage]);

  const openNewDialog = () => {
    setEditingRow(null);
    setForm({
      code: '',
      name: '',
      description: '',
      is_assignable: true,
      default_usage_type: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (row) => {
    setEditingRow(row);
    setForm({
      code: row.code || '',
      name: row.name || '',
      description: row.description || '',
      is_assignable: !!row.is_assignable,
      default_usage_type: row.default_usage_type || ''
    });
    setDialogOpen(true);
  };

  const handleFormChange = (field) => (event) => {
    const value =
      field === 'is_assignable' ? event.target.value === 'true' : event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!form.code || !form.name) {
      toast.error('Codice e nome categoria sono obbligatori');
      return;
    }
    try {
      const payload = {
        code: form.code,
        name: form.name,
        description: form.description || null,
        is_assignable: form.is_assignable,
        default_usage_type: form.default_usage_type || null
      };
      if (editingRow) {
        await api.put(`/asset-categories/${editingRow.id}`, payload);
        toast.success('Categoria asset aggiornata');
      } else {
        await api.post('/asset-categories', payload);
        toast.success('Categoria asset creata');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving asset category', error);
      toast.error('Errore nel salvataggio della categoria asset');
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/asset-categories/${deleteTarget.id}`);
      toast.success('Categoria asset eliminata');
      closeDeleteDialog();
      loadData();
    } catch (error) {
      console.error('Error deleting asset category', error);
      const msg =
        error?.response?.data?.message ||
        'Errore nella eliminazione della categoria asset';
      toast.error(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Tags size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Categorie asset
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestisci le categorie usate per classificare gli asset aziendali
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" size="small" onClick={openNewDialog}>
          <Plus size={16} style={{ marginRight: 8 }} />
          Nuova categoria
        </Button>
      </Box>

      <StyledCard>
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid rgba(145, 158, 171, 0.12)',
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <TextField
            size="small"
            placeholder="Cerca per codice, nome o descrizione..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === 'code' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'code'}
                    direction={orderBy === 'code' ? order : 'asc'}
                    onClick={() => handleRequestSort('code')}
                  >
                    Codice
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'name' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Assegnabile</TableCell>
                <TableCell>Uso predefinito</TableCell>
                <TableCell align="center">Totale asset</TableCell>
                <TableCell align="center">% sul totale</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredSortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nessuna categoria asset definita.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.code}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.description || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.is_assignable ? 'Sì' : 'No'}
                        size="small"
                        color={row.is_assignable ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{row.default_usage_type || '-'}</TableCell>
                    <TableCell align="center">
                      <StandardBadge>
                        {row.total_assets || 0}
                      </StandardBadge>
                    </TableCell>
                    <TableCell align="center">
                      {parseFloat(row.percentage || 0) > 0 ? (
                        <div style={{ position: 'relative', display: 'inline-flex' }}>
                          <CircularProgress
                            variant="determinate"
                            value={100}
                            size={36}
                            thickness={6}
                            sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                          />
                          <CircularProgress
                            variant="determinate"
                            value={parseFloat(row.percentage || 0)}
                            size={36}
                            thickness={6}
                            sx={{
                              position: 'absolute',
                              left: 0,
                              color: 'rgb(255, 171, 0)'
                            }}
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: 'absolute',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
                            >
                              {parseFloat(row.percentage || 0).toFixed(0)}%
                            </Typography>
                          </Box>
                        </div>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEditDialog(row)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(row)}
                      >
                        <DeleteIcon size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredSortedRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Righe per pagina:"
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingRow ? 'Modifica categoria asset' : 'Nuova categoria asset'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Codice"
              size="small"
              value={form.code}
              onChange={handleFormChange('code')}
              fullWidth
              required
            />
            <TextField
              label="Nome"
              size="small"
              value={form.name}
              onChange={handleFormChange('name')}
              fullWidth
              required
            />
            <TextField
              label="Descrizione"
              size="small"
              value={form.description}
              onChange={handleFormChange('description')}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Assegnabile</InputLabel>
              <Select
                label="Assegnabile"
                value={form.is_assignable ? 'true' : 'false'}
                onChange={handleFormChange('is_assignable')}
              >
                <MenuItem value="true">Sì</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Uso predefinito</InputLabel>
              <Select
                label="Uso predefinito"
                value={form.default_usage_type}
                onChange={handleFormChange('default_usage_type')}
              >
                <MenuItem value="">
                  <em>Nessuno</em>
                </MenuItem>
                <MenuItem value="SEDE">Uso in sede</MenuItem>
                <MenuItem value="SMART_WORKING">Smart working</MenuItem>
                <MenuItem value="TRASFERTA">Trasferta</MenuItem>
                <MenuItem value="MISTO">Misto</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSave}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Questa azione è irreversibile. Stai per eliminare la categoria:
          </DialogContentText>
          <Box sx={{ mt: 1, fontWeight: 700 }}>
            {deleteTarget?.name || '-'}
          </Box>
          <Box sx={{ color: 'text.secondary', fontSize: 14 }}>
            Codice: {deleteTarget?.code || '-'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>Annulla</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
